import {
  CashCloseCashOfferStageDisplayValues,
  CashCloseTradeInStageDisplayValues,
  ContactShape,
  PropertyOccupancyDisplayValues,
  PropertyTypeDisplayValues,
  TASK_CATEGORIES_OPTIONS_BY_OWNER,
  TASK_STAGE_OPTIONS_BY_OWNER,
  TaskOwner,
  humanizeLoanMilestoneName,
  humanizeLoanTypeName,
  humanizedCashCloseTypes,
  operatorHumanizeLoanProgramName,
  orderedLoanMilestones,
} from "@eave/shared/types/api";

import stateOptions from "web_modules/forms/data/stateOptions";
import { loanDecisions } from "web_modules/shared/loan_decisions";
import { loanStatusOptions, orderedLoanStatus } from "web_modules/shared/loan_statuses";

import {
  ColumnDef,
  defBoolean,
  defDate,
  defEnum,
  defMoney,
  defNumber,
  defText,
  defTime,
  EnumOption,
  orderFuncFromArray,
} from "web_modules/PipelineViewer";
import { selectableCOECategories } from "underwriter_dashboard/shared";
import { TRUSOperators } from "../../redux/getTrusOperators/types";

// Must provide the Trus operators from redux store for the LO, LM and UW filters to contain operators
const schema = (operators?: TRUSOperators | null) => [
  defNumber("loan_application_id", { displayName: "Loan application ID" }),
  defText("loan_id", { displayName: "Loan ID" }),

  defText("uli", { displayName: "ULI" }),
  defText("ma_nmls_id", { displayName: "MA NMLS ID" }),
  defText("denial_reason"),
  defMoney("total_loan_costs"),
  defMoney("lender_credits"),
  defNumber("total_units"),
  defText("aus_used", { displayName: "AUS" }),
  defText("aus_outcome", { displayName: "AUS outcome" }),

  /** == Snapshot/Cached data points ==
   * N.B. these values come from the appindexer.go and originate from a snapshot taken once a day
   */
  defDate("account_created_date"),
  defNumber("actual_gain"),
  defNumber("prepaid_interest"),
  defMoney("prepaid_interest_total_due", { displayName: "Prepaid Interest (FundingHurdle)" }),
  defNumber("secondary_market_accrued_interest"),
  defNumber("sales_adjustments_and_or_penalties"),
  defNumber("interest_income"),
  defNumber("origination_proceeds"),
  defNumber("other_revenue"),
  defNumber("curtailments"),
  defNumber("additional_fees"),
  defNumber("hedging_gains"),
  defMoney("available_funds", { displayName: "Usable assets" }),
  defText("borrower1_name", { displayName: "Borrower 1 name" }),
  defText("borrower1_first_name", { displayName: "Borrower 1 first name" }),
  defText("borrower1_last_name", { displayName: "Borrower 1 last name" }),
  defText("borrower1_middle_name", { displayName: "Borrower 1 middle name" }),
  defText("borrower1_suffix", { displayName: "Borrower 1 suffix" }),
  defText("borrower1_email", { displayName: "Borrower 1 email" }),
  defText("borrower1_ssn", { displayName: "Borrower 1 SSN" }),
  defText("borrower1_dob", { displayName: "Borrower 1 DOB" }),
  defText("borrower1_age", { displayName: "Borrower 1 Age" }),
  defText("borrower1_credit_score", { displayName: "Borrower 1 Credit Score" }),
  defText("borrower1_scoring_model", { displayName: "Borrower 1 Scoring Model" }),
  defText("borrower2_name", { displayName: "Borrower 2 name" }),
  defText("borrower2_first_name", { displayName: "Borrower 2 first name" }),
  defText("borrower2_last_name", { displayName: "Borrower 2 last name" }),
  defText("borrower2_middle_name", { displayName: "Borrower 2 middle name" }),
  defText("borrower2_suffix", { displayName: "Borrower 2 suffix" }),
  defText("borrower2_email", { displayName: "Borrower 2 email" }),
  defText("borrower2_ssn", { displayName: "Borrower 2 SSN" }),
  defText("borrower2_dob", { displayName: "Borrower 2 DOB" }),
  defText("borrower2_age", { displayName: "Borrower 2 Age" }),
  defText("borrower2_credit_score", { displayName: "Borrower 2 Credit Score" }),
  defText("borrower2_scoring_model", { displayName: "Borrower 2 Scoring Model" }),
  defText("borrower3_name", { displayName: "Borrower 3 name" }),
  defText("borrower3_first_name", { displayName: "Borrower 3 first name" }),
  defText("borrower3_last_name", { displayName: "Borrower 3 last name" }),
  defText("borrower3_middle_name", { displayName: "Borrower 3 middle name" }),
  defText("borrower3_suffix", { displayName: "Borrower 3 suffix" }),
  defText("borrower3_email", { displayName: "Borrower 3 email" }),
  defText("borrower3_ssn", { displayName: "Borrower 3 SSN" }),
  defText("borrower3_dob", { displayName: "Borrower 3 DOB" }),
  defText("borrower3_age", { displayName: "Borrower 3 Age" }),
  defText("borrower3_credit_score", { displayName: "Borrower 3 Credit Score" }),
  defText("borrower3_scoring_model", { displayName: "Borrower 3 Scoring Model" }),
  defText("borrower4_name", { displayName: "Borrower 4 name" }),
  defText("borrower4_first_name", { displayName: "Borrower 4 first name" }),
  defText("borrower4_last_name", { displayName: "Borrower 4 last name" }),
  defText("borrower4_middle_name", { displayName: "Borrower 4 middle name" }),
  defText("borrower4_suffix", { displayName: "Borrower 4 suffix" }),
  defText("borrower4_email", { displayName: "Borrower 4 email" }),
  defText("borrower4_ssn", { displayName: "Borrower 4 SSN" }),
  defText("borrower4_dob", { displayName: "Borrower 4 DOB" }),
  defText("borrower4_age", { displayName: "Borrower 4 Age" }),
  defText("borrower4_credit_score", { displayName: "Borrower 4 Credit Score" }),
  defText("borrower4_scoring_model", { displayName: "Borrower 4 Scoring Model" }),
  defMoney("borrower_estimated_loan_amount"),
  defMoney("borrower_estimated_purchase_price"),
  defText("buyer_real_estate_agent_name"),
  defText("buyer_real_estate_agent_email"),
  defText("buyer_real_estate_agent_company_name"),
  defText("buyer_real_estate_agent_company_email"),
  defMoney("computed_monthly_income", { displayName: "Monthly income" }), // Verified income only, no borrower estimated income
  defDate("conditionally_approved_date"),
  defNumber("dti", { displayName: "DTI (Back)" }),
  defNumber("dti_front", { displayName: "DTI (Front)" }),
  defNumber("effective_apr", { displayName: "APR" }),
  defNumber("effective_rate", { displayName: "Rate" }),
  defNumber("expected_gain"),
  defDate("first_payment_date"),
  defText("fnma_identifier", { displayName: "FNMA identifier" }),
  defNumber("investor_loan_number"),
  defText("investor_name"),
  defDate("investor_purchase_date"),
  defDate("investor_rate_lock_date"),
  defDate("investor_rate_lock_expiration_date"),
  defEnum(
    "loan_program_name",
    Object.entries(operatorHumanizeLoanProgramName).map(([value, label]) => ({
      value,
      label,
    })),
    { displayName: "Loan product" },
  ),
  defDate("loan_service_transfer_date"),
  defEnum(
    "loan_type",
    Object.keys(humanizeLoanTypeName).map(type => ({
      value: type,
      label: humanizeLoanTypeName[type],
    })),
    { displayName: "Loan program" },
  ),
  defNumber("ltv", { displayName: "LTV" }),
  defDate("mortgage_approved_date"),
  defText("mortgage_lead_id"),
  defNumber("order_id"),
  defMoney("purchase_price", { displayName: "Purchase price" }),
  defNumber("qualification_rate", { displayName: "Qualification rate" }),
  defText("subject_property_county"),
  defText("subject_property_address"),
  defText("warehouse_line"),

  /** Queried data points */
  defDate("all_trailing_docs_due_by", { displayName: "All trailing docs due" }),
  defDate("all_property_docs_due_by", { displayName: "All property docs due" }),
  defDate("application_date"),
  defDate("appraisal_approved"),
  defDate("appraisal_delivery_due_by", { displayName: "Appraisal delivery due" }),
  defDate("appraisal_expected"),
  defDate("appraisal_inspection_date"),
  defDate("appraisal_received"),
  defDate("appraisal_requested"),
  defTime("appraisal_sent"),
  defMoney("appraised_value"),
  defMoney("actual_revenue"),
  defMoney("expected_revenue"),
  defTime("appraisal_delivered"),
  defEnum(
    "cash_close_trade_in_stage",
    Object.entries(CashCloseTradeInStageDisplayValues).map(([value, label]) => ({
      value,
      label,
    })),
    { displayName: "Cash Close trade-in stage" },
  ),
  defEnum(
    "cash_close_cash_offer_stage",
    Object.entries(CashCloseCashOfferStageDisplayValues).map(([value, label]) => ({
      value,
      label,
    })),
    { displayName: "Cash Close cash offer stage" },
  ),
  defEnum(
    "property_type",
    Object.entries(PropertyTypeDisplayValues).map(([value, label]) => ({
      value,
      label,
    })),
    { displayName: "Property type" },
  ),
  defEnum(
    "property_occupancy",
    Object.entries(PropertyOccupancyDisplayValues).map(([value, label]) => ({
      value,
      label,
    })),
    { displayName: "Property occupancy" },
  ),
  defEnum(
    "assigned_loan_manager_name",
    operators
      ? operators.loan_managers.map((lm: ContactShape): EnumOption => {
          return {
            value: lm.name,
            label: lm.name,
          };
        })
      : [],
    { displayName: "Assigned LM name" },
  ),
  defEnum(
    "assigned_post_closer_name",
    operators
      ? operators.post_closers.map((pc: ContactShape): EnumOption => {
          return {
            value: pc.name,
            label: pc.name,
          };
        })
      : [],
    { displayName: "Assigned Post-closer name" },
  ),
  defEnum(
    "assigned_closer_funder_name",
    operators
      ? operators.closer_funders.map((cf: ContactShape): EnumOption => {
          return {
            value: cf.name,
            label: cf.name,
          };
        })
      : [],
    { displayName: "Assigned closer/funder name" },
  ),
  defEnum(
    "assigned_loan_officer_name",
    operators
      ? operators.loan_officers.map((lo: ContactShape): EnumOption => {
          return {
            value: lo.name,
            label: lo.name,
          };
        })
      : [],
    { displayName: "Assigned LO name" },
  ),
  defEnum(
    "assigned_underwriter_name",
    operators
      ? operators.underwriters.map((uw: ContactShape): EnumOption => {
          return {
            value: uw.name,
            label: uw.name,
          };
        })
      : [],
    { displayName: "Assigned UW name" },
  ),
  defDate("borrower_rate_lock_date"),
  defDate("borrower_rate_lock_expiration_date"),
  defEnum(
    "cash_close_type",
    Object.keys(humanizedCashCloseTypes).map(type => ({
      value: type,
      label: humanizedCashCloseTypes[type],
    })),
    { displayName: "Cash Close product" },
  ),
  defText("cash_trade_in_departing_residence", { displayName: "Trade-In departing residence" }),
  defEnum("loan_purpose", [
    { label: "Purchase", value: "Purchase" },
    { label: "Bridge", value: "Bridge" },
  ]),
  defDate("closing_date", { displayName: "COE Date" }),
  defEnum("closing_date_change_category", selectableCOECategories, {
    displayName: "COE Date Change Category",
  }),
  defMoney("loan_discount_points", { displayName: "Loan Discount Points" }),
  defText("closing_date_change_reason", { displayName: "COE Date Change Reason" }),
  defDate("closing_package_due_by", { displayName: "Closing package due" }),
  defTime("condition_response_due"),
  defNumber("conditions_pre_ready_to_review"),
  defNumber("document_requests_pre_ready_to_review", {
    displayName: "Document requests outstanding",
  }),
  defNumber("conditions_ready_to_review"),
  defDate("credit_report_date"),
  defNumber("credit_score"),
  defDate("disbursement_date"),
  defDate("initial_cd_due_by", { displayName: "Initial CD due" }),
  defDate("initial_le_due_by", { displayName: "Initial LE due" }),
  defTime("initial_le_sent", { displayName: "Initial LE sent" }),
  defTime("initial_le_delivered", { displayName: "Initial LE delivered" }),
  defTime("intent_to_proceed_due_by", { displayName: "Intent to proceed due" }),
  defTime("intent_to_proceed_receipt"),
  defBoolean("is_archived", { displayName: "Archived" }),
  defText("lead_number"),
  defEnum(
    "lm_task_category",
    TASK_CATEGORIES_OPTIONS_BY_OWNER[TaskOwner.LMLender].map(value => ({ value, label: value })),
    { displayName: "LM task category" },
  ),
  defDate("lm_task_due_date", { displayName: "LM task due date" }),
  defBoolean("lm_task_is_critical", { displayName: "Is critical LM task" }),
  defText("lm_task_name", { displayName: "LM next task" }),
  defEnum(
    "lm_task_stage",
    TASK_STAGE_OPTIONS_BY_OWNER[TaskOwner.LMLender].map(value => ({
      value,
      label: value,
    })),
    {
      displayName: "LM task stage",
      orderFunc: orderFuncFromArray(TASK_STAGE_OPTIONS_BY_OWNER[TaskOwner.LMLender], false),
    },
  ),
  defMoney("loan_amount"),
  defEnum(
    "loan_status",
    loanStatusOptions.filter(option => !option.deprecated),
    {
      orderFunc: orderFuncFromArray(orderedLoanStatus, false),
    },
  ),
  defDate("loan_status_date"),
  defEnum("loan_decision", loanDecisions),
  defDate("loan_decision_date"),
  defTime("message_response_due"),
  defNumber("messages_to_review"),
  defDate("noia_timestamp", { displayName: "NOIA timestamp" }),
  defDate("noia_followup", { displayName: "NOIA followup" }),
  defDate("noia_due_on", { displayName: "NOIA due" }),
  defNumber("outstanding_documents"),
  defText("pinned_message", { displayName: "Pinned note" }),
  defDate("post_closing_prep_due", { displayName: "Post-closing prep due" }),
  defTime("pre_app_aan_sent", { displayName: "Pre-app AAN sent" }),
  defTime("pre_app_underwrite_due", { displayName: "Pre-app underwrite due" }),
  defDate("pre_app_aan_due", { displayName: "Pre-app AAN due" }),
  defEnum(
    "property_state",
    stateOptions.map(state => ({
      ...state,
      label: state.label,
    })),
  ),
  defNumber("property_doc_outstanding_conditions_count"),
  defTime("purchased_property_received_date"),
  defDate("reg_b_credit_decision_due_by", { displayName: "Reg B credit decision due" }),
  defDate("registration_date"),
  defTime("revised_le_delivered", { displayName: "Revised LE delivered" }),
  defTime("revised_le_sent", { displayName: "Revised LE sent" }),
  defDate("revised_le_due", { displayName: "Revised LE due" }),
  defText("seller_real_estate_agent_name"),
  defText("seller_real_estate_agent_email"),
  defText("seller_real_estate_agent_company_name"),
  defText("seller_real_estate_agent_company_email"),
  defText("settlement_agent_name"),
  defText("settlement_agent_email"),
  defText("settlement_agent_company_name"),
  defText("settlement_agent_company_email"),
  defDate("target_closing_date", { displayName: "Target COE Date" }),
  defText("title_agent_name"),
  defText("title_agent_email"),
  defText("title_company_name"),
  defText("title_company_email"),
  defText("title_order_number"),
  defText("appraiser_contact_name", { displayName: "Appraiser Contact Name" }),
  defText("appraiser_contact_state_license", { displayName: "Appraiser Contact State License" }),
  defText("appraiser_company_name", { displayName: "Appraiser Company Name" }),
  defDate("servicing_transfer_due_by", { displayName: "Servicing transfer due" }),
  defDate("servicing_transfer_completed"),
  defNumber("uw_task_count", { displayName: "UW task count" }),
  defDate("uw_task_first_due", { displayName: "UW task due" }),
  defText("uw_next_task", { displayName: "UW next task" }),
  defText("property_zip_code", { displayName: "Property ZIP code" }),

  defTime("underwriter_assignment_time", { displayName: "Underwriter assigned at" }),
  defTime("loan_manager_assignment_time", { displayName: "Loan Manager assigned at" }),
  defTime("closer_funder_assignment_time", { displayName: "Closer/Funder assigned at" }),
  defTime("post_closer_assignment_time", { displayName: "Post-Closer assigned at" }),

  defEnum(
    "loan_milestone",
    Object.keys(humanizeLoanMilestoneName).map(milestone => ({
      value: milestone,
      label: humanizeLoanMilestoneName[milestone],
    })),
    { orderFunc: orderFuncFromArray(orderedLoanMilestones, false) },
  ),
];

export const convertToSlug = (str: String) =>
  str
    .replace(/ /g, "-")
    .replace(/[^\w-]+/g, "")
    .toLowerCase();

export function getSchema(visibleFields: string[], operators?: TRUSOperators): ColumnDef[] {
  const allSchemaFields = schema(operators);
  return allSchemaFields.filter(f => visibleFields.includes(f.jsonKey));
}
